import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SearchIcon from "@mui/icons-material/Search";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlaceIcon from "@mui/icons-material/Place";
import BusinessIcon from "@mui/icons-material/Business";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneIcon from "@mui/icons-material/Phone";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  collection,
  onSnapshot,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

export interface BranchOffice {
  id: string;
  name: string;
  city: string;
  address: string;
  schedule: string;
  phone?: string;
  active: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export interface Appointment {
  id?: string;
  code: string;
  clientName: string;
  documentType: string;
  documentNumber: string;
  phone: string;
  email: string;
  modality: "presencial" | "virtual";
  branch?: string;
  branchId?: string;
  meetUrl?: string;
  meetCode?: string;
  vertical: string;
  serviceName: string;
  date: string; // YYYY-MM-DD
  timeSlot: string; // e.g. "09:00 AM"
  notes?: string;
  status: "pendiente" | "aceptada" | "cancelada" | "completada";
  createdAt?: any;
  updatedAt?: any;
}

const statusColors: Record<string, "warning" | "success" | "error" | "info" | "default"> = {
  pendiente: "warning",
  aceptada: "success",
  cancelada: "error",
  completada: "info",
};

const statusLabels: Record<string, string> = {
  pendiente: "Pendiente",
  aceptada: "Aceptada / Confirmada",
  cancelada: "Cancelada",
  completada: "Completada",
};

const DEFAULT_BRANCHES: Omit<BranchOffice, "id">[] = [
  {
    name: "Sede Principal Bogotá",
    city: "Bogotá D.C.",
    address: "Calle 100 # 15-20, Torre Financiera, Piso 8",
    schedule: "Lunes a Viernes 8:00 AM - 5:00 PM, Sábados 8:00 AM - 12:00 PM",
    phone: "+57 (601) 745-0000",
    active: true,
  },
  {
    name: "Sede Medellín - El Poblado",
    city: "Medellín",
    address: "Carrera 43A # 5A-113, Centro Empresarial, Oficina 402",
    schedule: "Lunes a Viernes 8:00 AM - 5:00 PM, Sábados 8:00 AM - 12:00 PM",
    phone: "+57 (604) 604-1111",
    active: true,
  },
  {
    name: "Sede Cali - Granada",
    city: "Cali",
    address: "Avenida 9N # 12N-35, Edificio Corporativo, Piso 3",
    schedule: "Lunes a Viernes 8:00 AM - 5:00 PM",
    phone: "+57 (602) 889-2222",
    active: true,
  },
];

export default function DistripakoCitasPage() {
  // Main Navigation View: Citas vs Sedes vs Configuración de Llamadas
  const [mainTab, setMainTab] = useState<"citas" | "sedes">("citas");

  // Appointments State
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  // Sedes State
  const [sedes, setSedes] = useState<BranchOffice[]>([]);
  const [loadingSedes, setLoadingSedes] = useState(true);
  const [openSedeDialog, setOpenSedeDialog] = useState(false);
  const [editingSede, setEditingSede] = useState<BranchOffice | null>(null);

  // Sede Form State
  const [sedeName, setSedeName] = useState("");
  const [sedeCity, setSedeCity] = useState("");
  const [sedeAddress, setSedeAddress] = useState("");
  const [sedeSchedule, setSedeSchedule] = useState("");
  const [sedePhone, setSedePhone] = useState("");
  const [sedeActive, setSedeActive] = useState(true);

  // Corporate Video Call Settings State
  const [corporateMeetUrl, setCorporateMeetUrl] = useState("");
  const [savingMeetUrl, setSavingMeetUrl] = useState(false);

  // Calendly Customer Booking Link State
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [savingCalendlyUrl, setSavingCalendlyUrl] = useState(false);

  // Agenda Public Schedule Configuration State
  const [scheduleConfig, setScheduleConfig] = useState({
    startHour: "08:00",
    endHour: "18:00",
    slotDuration: 30, // minutes
    allowWeekdays: true,
    allowSaturdays: false,
    allowSundays: false,
  });
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Accept Appointment Dialog State
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [appointmentToAccept, setAppointmentToAccept] = useState<Appointment | null>(null);
  const [assignedCallUrl, setAssignedCallUrl] = useState("");
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const [processingAccept, setProcessingAccept] = useState(false);

  // Snackbar feedback
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Calendar State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDayFilter, setSelectedDayFilter] = useState<string | null>(null);

  // Tab & Filters
  const [currentTab, setCurrentTab] = useState<string>("todas");
  const [modalityFilter, setModalityFilter] = useState<string>("todas");
  const [searchTerm, setSearchTerm] = useState("");

  // Detail Dialog State
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);

  // Subscribe to collection "citas"
  useEffect(() => {
    const colRef = collection(db, "citas");
    const unsub = onSnapshot(
      colRef,
      (snapshot) => {
        const list: Appointment[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Appointment, "id">),
        }));

        list.sort((a, b) => {
          const dateComp = (b.date || "").localeCompare(a.date || "");
          if (dateComp !== 0) return dateComp;
          return (a.timeSlot || "").localeCompare(b.timeSlot || "");
        });

        setAppointments(list);
        setLoading(false);
      },
      (error) => {
        console.error("Error subscribing to citas:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // Subscribe to collection "sedes"
  useEffect(() => {
    const colRef = collection(db, "sedes");
    const unsub = onSnapshot(
      colRef,
      (snapshot) => {
        const list: BranchOffice[] = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name || "",
            city: data.city || "",
            address: data.address || "",
            schedule: data.schedule || "",
            phone: data.phone || "",
            active: data.active !== false,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          };
        });

        list.sort((a, b) => a.city.localeCompare(b.city) || a.name.localeCompare(b.name));
        setSedes(list);
        setLoadingSedes(false);
      },
      (error) => {
        console.error("Error subscribing to sedes:", error);
        setLoadingSedes(false);
      }
    );

    return () => unsub();
  }, []);

  // Load corporate video call config from Firestore
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const confDoc = await getDoc(doc(db, "config_general", "videollamadas"));
        if (confDoc.exists()) {
          const data = confDoc.data();
          if (data.corporateMeetUrl) {
            setCorporateMeetUrl(data.corporateMeetUrl);
          }
        }

        const agendaDoc = await getDoc(doc(db, "config_general", "agenda_config"));
        if (agendaDoc.exists()) {
          const aData = agendaDoc.data();
          setScheduleConfig({
            startHour: aData.startHour || "08:00",
            endHour: aData.endHour || "18:00",
            slotDuration: aData.slotDuration || 30,
            allowWeekdays: aData.allowWeekdays !== false,
            allowSaturdays: Boolean(aData.allowSaturdays),
            allowSundays: Boolean(aData.allowSundays),
          });
        }
      } catch (err) {
        console.error("Error loading videollamada config:", err);
      }
    };
    loadConfig();
  }, []);

  // Calendar helper functions
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonthIndex = new Date(year, month, 1).getDay();

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
    setSelectedDayFilter(null);
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
    setSelectedDayFilter(null);
  };
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDayFilter(null);
  };

  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  // Group appointments by date (includes full appointment list per day)
  const appointmentsByDate = useMemo(() => {
    const map: Record<string, { total: number; pendientes: number; aceptadas: number; items: Appointment[] }> = {};
    appointments.forEach((appt) => {
      if (!appt.date) return;
      if (!map[appt.date]) {
        map[appt.date] = { total: 0, pendientes: 0, aceptadas: 0, items: [] };
      }
      map[appt.date].total++;
      map[appt.date].items.push(appt);
      if (appt.status === "pendiente") map[appt.date].pendientes++;
      if (appt.status === "aceptada") map[appt.date].aceptadas++;
    });
    // Sort items within each day by timeSlot
    Object.values(map).forEach((day) => {
      day.items.sort((a, b) => (a.timeSlot || "").localeCompare(b.timeSlot || ""));
    });
    return map;
  }, [appointments]);

  const todayStr = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  // Stats Counters
  const stats = useMemo(() => {
    const total = appointments.length;
    const pendientes = appointments.filter((a) => a.status === "pendiente").length;
    const aceptadas = appointments.filter((a) => a.status === "aceptada").length;
    const completadas = appointments.filter((a) => a.status === "completada").length;
    const canceladas = appointments.filter((a) => a.status === "cancelada").length;
    const hoy = appointments.filter((a) => a.date === todayStr).length;
    return { total, pendientes, aceptadas, completadas, canceladas, hoy };
  }, [appointments, todayStr]);

  // Filtered Appointments
  const filteredAppointments = useMemo(() => {
    return appointments.filter((a) => {
      if (currentTab === "pendientes" && a.status !== "pendiente") return false;
      if (currentTab === "aceptadas" && a.status !== "aceptada") return false;
      if (currentTab === "completadas" && a.status !== "completada") return false;
      if (currentTab === "canceladas" && a.status !== "cancelada") return false;
      if (currentTab === "hoy" && a.date !== todayStr) return false;
      if (modalityFilter !== "todas" && a.modality !== modalityFilter) return false;
      if (selectedDayFilter && a.date !== selectedDayFilter) return false;

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchesName = a.clientName?.toLowerCase().includes(term);
        const matchesCode = a.code?.toLowerCase().includes(term);
        const matchesDoc = a.documentNumber?.toLowerCase().includes(term);
        const matchesService = a.serviceName?.toLowerCase().includes(term);
        if (!matchesName && !matchesCode && !matchesDoc && !matchesService) {
          return false;
        }
      }

      return true;
    });
  }, [appointments, currentTab, modalityFilter, selectedDayFilter, searchTerm, todayStr]);

  // Save Corporate Video Call URL in Firestore
  const handleSaveCorporateMeetUrl = async () => {
    setSavingMeetUrl(true);
    try {
      await setDoc(doc(db, "config_general", "videollamadas"), {
        corporateMeetUrl: corporateMeetUrl.trim(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSnackbar({ open: true, message: "Enlace corporativo de videollamada guardado exitosamente", severity: "success" });
    } catch (err) {
      console.error("Error saving corporate meet URL:", err);
      setSnackbar({ open: true, message: "Error al guardar enlace corporativo", severity: "error" });
    } finally {
      setSavingMeetUrl(false);
    }
  };

  // Save Agenda Schedule Config in Firestore
  const handleSaveScheduleConfig = async () => {
    setSavingSchedule(true);
    try {
      await setDoc(doc(db, "config_general", "agenda_config"), {
        ...scheduleConfig,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSnackbar({
        open: true,
        message: "¡Horarios de atención de la agenda guardados con éxito! Se sincronizaron con next-front.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving schedule config:", err);
      setSnackbar({ open: true, message: "Error al guardar la configuración de horarios", severity: "error" });
    } finally {
      setSavingSchedule(false);
    }
  };

  // Status Change Handlers
  const handleUpdateStatus = async (id: string, newStatus: Appointment["status"]) => {
    try {
      await updateDoc(doc(db, "citas", id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      if (selectedAppointment && selectedAppointment.id === id) {
        setSelectedAppointment((prev) => prev ? { ...prev, status: newStatus } : null);
      }

      // Si se cancela la cita, eliminar automáticamente de Google Calendar y enviar correo
      if (newStatus === "cancelada") {
        const appt = appointments.find((a) => a.id === id) || selectedAppointment;
        if (appt) {
          fetch("http://localhost:3000/api/citas/cancel-event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              appointmentId: id,
              calendarEventId: (appt as any).calendarEventId,
              code: appt.code,
              clientName: appt.clientName,
              email: appt.email,
              serviceName: appt.serviceName,
              date: appt.date,
              timeSlot: appt.timeSlot,
              reason: "Cancelada por el administrador",
            }),
          }).catch((err) => console.warn("Error sincronizando cancelación en Google Calendar:", err));
        }
      }

      setSnackbar({ open: true, message: `Estado de cita actualizado a ${newStatus}`, severity: "success" });
    } catch (err) {
      console.error("Error updating status:", err);
      setSnackbar({ open: true, message: "Error al actualizar el estado de la cita", severity: "error" });
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Estás seguro de eliminar permanentemente esta cita del sistema? También se removerá de Google Calendar.")) {
      try {
        const appt = appointments.find((a) => a.id === id) || selectedAppointment;
        if (appt) {
          fetch("http://localhost:3000/api/citas/cancel-event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              appointmentId: id,
              calendarEventId: (appt as any).calendarEventId,
              code: appt.code,
              clientName: appt.clientName,
              email: appt.email,
              serviceName: appt.serviceName,
              date: appt.date,
              timeSlot: appt.timeSlot,
              reason: "Eliminada del sistema por el administrador",
            }),
          }).catch((err) => console.warn("Error borrando de Google Calendar:", err));
        }

        await deleteDoc(doc(db, "citas", id));
        if (openDetailDialog) setOpenDetailDialog(false);
        setSnackbar({ open: true, message: "Cita eliminada correctamente de la base de datos y Google Calendar", severity: "info" });
      } catch (err) {
        console.error("Error deleting appointment:", err);
        setSnackbar({ open: true, message: "Error al eliminar la cita", severity: "error" });
      }
    }
  };

  const copyToClipboard = (text: string, label = "Enlace") => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label} copiado al portapapeles`, severity: "success" });
  };

  // Open Accept Modal
  const handleInitiateAcceptAppointment = (appt: Appointment) => {
    setAppointmentToAccept(appt);
    // Suggest URL: existing meetUrl OR corporateMeetUrl OR reliable instant room
    const cleanCode = (appt.code || "cita").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const suggestedUrl = appt.meetUrl || corporateMeetUrl.trim() || `https://meet.jit.si/GrupoNext-${cleanCode}`;
    setAssignedCallUrl(suggestedUrl);
    setSendEmailNotification(true);
    setOpenAcceptDialog(true);
  };

  // Confirm Appointment and Send Email + Notification
  const handleConfirmAndNotify = async () => {
    if (!appointmentToAccept) return;
    setProcessingAccept(true);

    const callUrl = assignedCallUrl.trim();
    const appt = appointmentToAccept;

    try {
      // 1. Update Firestore Appointment
      await updateDoc(doc(db, "citas", appt.id!), {
        status: "aceptada",
        meetUrl: callUrl,
        updatedAt: serverTimestamp(),
      });

      // 2. Queue Email in Firestore collection "mail"
      if (sendEmailNotification && appt.email) {
        try {
          await addDoc(collection(db, "mail"), {
            to: appt.email,
            message: {
              subject: `¡Cita Confirmada! ${appt.serviceName} - Grupo Next (${appt.code})`,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; padding: 24px; border: 1px solid #e2e8f0; border-radius: 16px;">
                  <h2 style="color: #1e3a8a;">¡Tu Cita ha sido Confirmada!</h2>
                  <p>Hola <strong>${appt.clientName}</strong>,</p>
                  <p>Te informamos que tu cita de <strong>${appt.serviceName}</strong> ha sido <strong>confirmada</strong> por nuestro equipo de especialistas.</p>
                  
                  <div style="background-color: #f8fafc; padding: 16px; border-radius: 12px; margin: 20px 0;">
                    <p style="margin: 4px 0;"><strong>Código de Cita:</strong> ${appt.code}</p>
                    <p style="margin: 4px 0;"><strong>Fecha:</strong> ${appt.date}</p>
                    <p style="margin: 4px 0;"><strong>Horario:</strong> ${appt.timeSlot}</p>
                    <p style="margin: 4px 0;"><strong>Modalidad:</strong> ${appt.modality === "virtual" ? "Videollamada en Línea" : `Presencial en ${appt.branch || ""}`}</p>
                  </div>

                  ${appt.modality === "virtual" && callUrl ? `
                    <div style="text-align: center; margin: 30px 0;">
                      <a href="${callUrl}" target="_blank" style="background-color: #2563eb; color: #ffffff; padding: 14px 28px; text-decoration: none; border-radius: 10px; font-weight: bold; font-size: 15px; display: inline-block;">
                        💻 Entrar a la Videollamada
                      </a>
                      <p style="font-size: 12px; color: #64748b; margin-top: 10px;">Enlace directo: <a href="${callUrl}">${callUrl}</a></p>
                    </div>
                  ` : ""}

                  <p style="font-size: 12px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 16px;">
                    Grupo Next - Atención Integral Financiera & Especialidades.
                  </p>
                </div>
              `,
              text: `Hola ${appt.clientName}. Tu cita (${appt.code}) ha sido confirmada para el ${appt.date} a las ${appt.timeSlot}. Enlace de llamada: ${callUrl}`,
            },
            createdAt: serverTimestamp(),
          });
        } catch (mailErr) {
          console.warn("Could not write to mail collection:", mailErr);
        }

        // 3. Also open client's default email app via mailto pre-filled for 100% guarantee
        const subject = encodeURIComponent(`Cita Confirmada: ${appt.serviceName} - Grupo Next (${appt.code})`);
        const body = encodeURIComponent(
          `Hola ${appt.clientName},\n\n` +
          `Tu cita de ${appt.serviceName} ha sido confirmada para el ${appt.date} a las ${appt.timeSlot}.\n\n` +
          (appt.modality === "virtual" && callUrl ? `💻 Enlace de la videollamada: ${callUrl}\n\nPor favor conéctate puntualmente.\n\n` : "") +
          `Código de Cita: ${appt.code}\n\nGrupo Next`
        );
        window.open(`mailto:${appt.email}?subject=${subject}&body=${body}`, "_blank");
      }

      setSnackbar({
        open: true,
        message: `¡Cita ${appt.code} aceptada exitosamente! Notificación enviada.`,
        severity: "success",
      });
      setOpenAcceptDialog(false);
    } catch (err) {
      console.error("Error accepting appointment:", err);
      setSnackbar({ open: true, message: "Error al confirmar la cita", severity: "error" });
    } finally {
      setProcessingAccept(false);
    }
  };

  // Enviar WhatsApp oficial de Bienvenida y Requisitos de Crédito
  const openCreditOnboardingWhatsApp = (appt: Appointment) => {
    const cleanPhone = (appt.phone || "").replace(/\D/g, "");
    const phoneWithCode = cleanPhone.startsWith("57") ? cleanPhone : `57${cleanPhone}`;
    const msg =
`BIENVENIDOS A NEXT CONNECTION GROUP SAS
Empresa especialista en Finanzas, Seguros e Inversiones.
El propósito de este grupo es tener la trazabilidad de todo el proceso de crédito con nuestra compañía y brindarles el mejor servicio.

1. Para iniciar con el trámite del estudio de crédito:
Este tendrá un costo de $ 250.000 mil pesos, que incluye consulta y perfilamiento de cliente en Datacrédito, verificación de documentos, y análisis financiero, para perfilar el crédito con las diferentes entidades financieras, bancarias o cooperativas.
Por favor consignar el estudio de crédito $250.000, a Cuenta Corriente # 29899994884 BANCOLOMBIA NIT 901500993.

2. Se anexa CONTRATO DE PRESTACION DE SERVICIOS DE NEXT para firma del cliente.

3. Los documentos requeridos para el estudio de crédito son:

De la Empresa:
• Copia de cédula al 150 % del Representante legal
• Rut actualizado
• Cámara de comercio actualizada
• Dos últimas declaraciones de renta
• Balances y estados de resultados dos últimos periodos fiscales y corte comercial, a Dic 2023, Dic 2024, dic 2025 y corte junio 2026 con sus respectivas notas.
• Declaraciones de IVA 2026
• Composición accionaria actualizada
• Tarjeta profesional del contador
• Extractos bancarios último trimestre
• Estatutos y reformas
• Dictamen de revisor fiscal
• Formulario Beneficiario final de la DIAN

AVALISTA SOCIO CON PARTICIPACION SUPERIOR AL 50%:
• Copia de cédula al 150 % con firma y huella
• Rut actualizado
• Declaración de renta 2023, 2024
• Anexos a la declaración de renta
• Balances y estados de resultados dos últimos periodos fiscales y corte comercial, Dic 2023, Dic 2024 y último corte Dic de 2025 con sus respectivas notas.
• Extractos bancarios último trimestre

Portal de autogestión de crédito y formalización: https://nextconnection.co/portal/credito`;

    window.open(`https://wa.me/${phoneWithCode}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // WhatsApp helper
  const openWhatsApp = (appt: Appointment) => {
    const cleanPhone = (appt.phone || "").replace(/\D/g, "");
    const phoneWithCode = cleanPhone.startsWith("57") ? cleanPhone : `57${cleanPhone}`;
    const modalityText = appt.modality === "presencial" ? `presencial en ${appt.branch || "nuestra sede"}` : "virtual por videollamada";
    let msg = `Hola ${appt.clientName}, te contactamos de Grupo Next respecto a tu cita (${appt.code}) de ${appt.serviceName} programada de forma ${modalityText} para el día ${appt.date} a las ${appt.timeSlot}.`;
    
    if (appt.modality === "virtual" && appt.meetUrl) {
      msg += `\n\n💻 Tu enlace directo para conectarte a la videollamada es:\n${appt.meetUrl}\nPor favor conéctate puntualmente a la hora acordada.`;
    }

    window.open(`https://wa.me/${phoneWithCode}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Send Direct Email via Mailto
  const openMailto = (appt: Appointment) => {
    const subject = encodeURIComponent(`Detalles de Cita Grupo Next - ${appt.serviceName} (${appt.code})`);
    const body = encodeURIComponent(
      `Hola ${appt.clientName},\n\n` +
      `Te contactamos de Grupo Next respecto a tu cita (${appt.code}) de ${appt.serviceName} programada para el día ${appt.date} a las ${appt.timeSlot}.\n\n` +
      (appt.modality === "virtual" && appt.meetUrl ? `💻 Enlace de la videollamada: ${appt.meetUrl}\n\n` : "") +
      `Atentamente,\nEquipo Grupo Next`
    );
    window.open(`mailto:${appt.email}?subject=${subject}&body=${body}`, "_blank");
  };

  // ================= SEDES CRUD HANDLERS =================
  const handleOpenNewSede = () => {
    setEditingSede(null);
    setSedeName("");
    setSedeCity("");
    setSedeAddress("");
    setSedeSchedule("Lunes a Viernes 8:00 AM - 5:00 PM, Sábados 8:00 AM - 12:00 PM");
    setSedePhone("");
    setSedeActive(true);
    setOpenSedeDialog(true);
  };

  const handleOpenEditSede = (sede: BranchOffice) => {
    setEditingSede(sede);
    setSedeName(sede.name);
    setSedeCity(sede.city);
    setSedeAddress(sede.address);
    setSedeSchedule(sede.schedule);
    setSedePhone(sede.phone || "");
    setSedeActive(sede.active !== false);
    setOpenSedeDialog(true);
  };

  const handleSaveSede = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sedeName.trim() || !sedeCity.trim() || !sedeAddress.trim()) {
      alert("Por favor completa el nombre, la ciudad y la dirección de la sede.");
      return;
    }

    try {
      if (editingSede) {
        await updateDoc(doc(db, "sedes", editingSede.id), {
          name: sedeName.trim(),
          city: sedeCity.trim(),
          address: sedeAddress.trim(),
          schedule: sedeSchedule.trim(),
          phone: sedePhone.trim(),
          active: sedeActive,
          updatedAt: serverTimestamp(),
        });
        setSnackbar({ open: true, message: "Sede actualizada correctamente", severity: "success" });
      } else {
        await addDoc(collection(db, "sedes"), {
          name: sedeName.trim(),
          city: sedeCity.trim(),
          address: sedeAddress.trim(),
          schedule: sedeSchedule.trim(),
          phone: sedePhone.trim(),
          active: sedeActive,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setSnackbar({ open: true, message: "Nueva sede creada exitosamente", severity: "success" });
      }
      setOpenSedeDialog(false);
    } catch (err) {
      console.error("Error saving sede:", err);
      setSnackbar({ open: true, message: "Error al guardar la sede", severity: "error" });
    }
  };

  const handleToggleSedeActive = async (sede: BranchOffice) => {
    try {
      const newActive = !sede.active;
      await updateDoc(doc(db, "sedes", sede.id), {
        active: newActive,
        updatedAt: serverTimestamp(),
      });
      setSnackbar({
        open: true,
        message: `Sede ${sede.name} ${newActive ? "habilitada" : "deshabilitada"} para visitas`,
        severity: "info",
      });
    } catch (err) {
      console.error("Error toggling sede active:", err);
      setSnackbar({ open: true, message: "Error al cambiar estado de la sede", severity: "error" });
    }
  };

  const handleDeleteSede = async (id: string, name: string) => {
    if (window.confirm(`¿Estás seguro de eliminar permanentemente la sede "${name}"? Ya no aparecerá disponible para citas presenciales.`)) {
      try {
        await deleteDoc(doc(db, "sedes", id));
        setSnackbar({ open: true, message: "Sede eliminada del catálogo", severity: "info" });
      } catch (err) {
        console.error("Error deleting sede:", err);
        setSnackbar({ open: true, message: "Error al eliminar la sede", severity: "error" });
      }
    }
  };

  const handleSeedDefaultSedes = async () => {
    if (window.confirm("¿Deseas registrar las sedes principales predeterminadas (Bogotá, Medellín, Cali) en Firestore?")) {
      try {
        for (const def of DEFAULT_BRANCHES) {
          await addDoc(collection(db, "sedes"), {
            ...def,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
        setSnackbar({ open: true, message: "Sedes predeterminadas creadas exitosamente", severity: "success" });
      } catch (err) {
        console.error("Error seeding sedes:", err);
        setSnackbar({ open: true, message: "Error al cargar sedes predeterminadas", severity: "error" });
      }
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1350, mx: "auto" }}>
      {/* Top Header */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={2} mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800} display="flex" alignItems="center" gap={1.5}>
            <CalendarMonthIcon color="primary" sx={{ fontSize: 32 }} />
            Portal Administrativo de Citas & Sedes
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Monitorea en tiempo real las citas virtuales y presenciales, confirma solicitudes con envío automático del enlace y administra las sedes.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          {mainTab === "citas" && (
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={goToToday}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              Ir a Hoy
            </Button>
          )}

          {mainTab === "sedes" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenNewSede}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, px: 2.5 }}
            >
              Nueva Sede
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Main Mode Navigation (Citas vs Sedes) */}
      <Paper sx={{ mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }} elevation={0}>
        <Tabs
          value={mainTab}
          onChange={(_, val) => setMainTab(val)}
          sx={{
            bgcolor: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
            "& .MuiTab-root": {
              fontWeight: 800,
              fontSize: "0.95rem",
              textTransform: "none",
              minHeight: 52,
              px: 3,
            },
          }}
        >
          <Tab
            value="citas"
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <EventAvailableIcon fontSize="small" />
                <span>Agenda de Citas</span>
                <Chip label={appointments.length} size="small" color="primary" sx={{ height: 20, fontSize: "11px", fontWeight: 800 }} />
              </Stack>
            }
          />
          <Tab
            value="sedes"
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <StorefrontIcon fontSize="small" />
                <span>Configuración de Sedes</span>
                <Chip label={sedes.filter((s) => s.active).length} size="small" color="success" sx={{ height: 20, fontSize: "11px", fontWeight: 800 }} />
              </Stack>
            }
          />
        </Tabs>
      </Paper>

      {/* ========================================================================= */}
      {/* ======================= TAB 1: AGENDA DE CITAS ========================== */}
      {/* ========================================================================= */}
      {mainTab === "citas" && (
        <>
          {/* Configuración de Horarios de la Agenda Pública (next-front) */}
          <Card
            sx={{
              borderRadius: "24px",
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              mb: 3,
              p: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
            }}
            elevation={0}
          >
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-start" }} spacing={2.5}>
              <Box>
                <Typography variant="subtitle1" fontWeight={800} color="#0d0f12" display="flex" alignItems="center" gap={1}>
                  <AccessTimeIcon sx={{ fontSize: 22, color: "#0d0f12" }} />
                  Horarios de Atención de la Agenda Pública (next-front)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.82rem" }}>
                  Ajusta de qué horas a qué horas y qué días (entre semana o fin de semana) se pueden agendar reuniones. Los clientes verán los horarios ocupados bloqueados en tiempo real.
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={handleSaveScheduleConfig}
                disabled={savingSchedule}
                sx={{
                  borderRadius: "9999px",
                  bgcolor: "#0d0f12",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  boxShadow: "0 4px 12px rgba(13, 15, 18, 0.15)",
                  "&:hover": { bgcolor: "#24272c" },
                  shrink: 0,
                }}
              >
                {savingSchedule ? "Guardando..." : "Guardar Horarios"}
              </Button>
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            <Grid container spacing={3} alignItems="center">
              {/* Hora Inicio */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Hora de Inicio</InputLabel>
                  <Select
                    value={scheduleConfig.startHour}
                    label="Hora de Inicio"
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, startHour: e.target.value })}
                    sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                  >
                    {["06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00"].map((h) => (
                      <MenuItem key={h} value={h}>{h} AM</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Hora Fin */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Hora de Fin</InputLabel>
                  <Select
                    value={scheduleConfig.endHour}
                    label="Hora de Fin"
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, endHour: e.target.value })}
                    sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                  >
                    {["14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00"].map((h) => {
                      const hNum = parseInt(h.split(":")[0]);
                      const pmHour = hNum > 12 ? hNum - 12 : hNum;
                      return <MenuItem key={h} value={h}>{h} ({pmHour}:00 PM)</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Grid>

              {/* Duración de cita */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Duración de Cita</InputLabel>
                  <Select
                    value={scheduleConfig.slotDuration}
                    label="Duración de Cita"
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, slotDuration: Number(e.target.value) })}
                    sx={{ borderRadius: "12px", bgcolor: "#f8fafc" }}
                  >
                    <MenuItem value={30}>30 Minutos</MenuItem>
                    <MenuItem value={45}>45 Minutos</MenuItem>
                    <MenuItem value={60}>1 Hora (60 min)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Días Habilitados */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={scheduleConfig.allowWeekdays}
                        onChange={(e) => setScheduleConfig({ ...scheduleConfig, allowWeekdays: e.target.checked })}
                        size="small"
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#0d0f12" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#0d0f12" } }}
                      />
                    }
                    label={<Typography variant="caption" fontWeight={700}>Entre semana</Typography>}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={scheduleConfig.allowSaturdays}
                        onChange={(e) => setScheduleConfig({ ...scheduleConfig, allowSaturdays: e.target.checked })}
                        size="small"
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#0d0f12" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#0d0f12" } }}
                      />
                    }
                    label={<Typography variant="caption" fontWeight={700}>Sábados</Typography>}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={scheduleConfig.allowSundays}
                        onChange={(e) => setScheduleConfig({ ...scheduleConfig, allowSundays: e.target.checked })}
                        size="small"
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#0d0f12" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#0d0f12" } }}
                      />
                    }
                    label={<Typography variant="caption" fontWeight={700}>Domingos</Typography>}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Card>

          {/* KPI Cards */}
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>TOTAL CITAS</Typography>
                  <Typography variant="h5" fontWeight={900} color="text.primary">{stats.total}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ borderRadius: 3, border: "1px solid #fef08a", bgcolor: "#fefce8", boxShadow: "0 2px 8px rgba(234,179,8,0.1)" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="caption" color="#854d0e" fontWeight={700}>🟡 PENDIENTES</Typography>
                  <Typography variant="h5" fontWeight={900} color="#a16207">{stats.pendientes}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ borderRadius: 3, border: "1px solid #bbf7d0", bgcolor: "#f0fdf4", boxShadow: "0 2px 8px rgba(34,197,94,0.1)" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="caption" color="#166534" fontWeight={700}>🟢 ACEPTADAS</Typography>
                  <Typography variant="h5" fontWeight={900} color="#15803d">{stats.aceptadas}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ borderRadius: 3, border: "1px solid #bae6fd", bgcolor: "#f0f9ff", boxShadow: "0 2px 8px rgba(14,165,233,0.1)" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="caption" color="#0369a1" fontWeight={700}>🔵 COMPLETADAS</Typography>
                  <Typography variant="h5" fontWeight={900} color="#0284c7">{stats.completadas}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ borderRadius: 3, border: "1px solid #fecaca", bgcolor: "#fef2f2", boxShadow: "0 2px 8px rgba(239,68,68,0.1)" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="caption" color="#991b1b" fontWeight={700}>🔴 CANCELADAS</Typography>
                  <Typography variant="h5" fontWeight={900} color="#b91c1c">{stats.canceladas}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Card sx={{ borderRadius: 3, border: "1px solid #e0e7ff", bgcolor: "#eef2ff", boxShadow: "0 2px 8px rgba(99,102,241,0.1)" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Typography variant="caption" color="#3730a3" fontWeight={700}>📅 CITAS HOY</Typography>
                  <Typography variant="h5" fontWeight={900} color="#4338ca">{stats.hoy}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Calendar View */}
          <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0", mb: 4, overflow: "hidden" }}>
            <Box sx={{ p: 2, px: 3, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton onClick={prevMonth} size="small">
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={800} minWidth={180} textAlign="center">
                  {monthNames[month]} {year}
                </Typography>
                <IconButton onClick={nextMonth} size="small">
                  <ChevronRightIcon />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={1.5} alignItems="center">
                {selectedDayFilter && (
                  <Chip
                    label={`Filtrando por día: ${selectedDayFilter}`}
                    onDelete={() => setSelectedDayFilter(null)}
                    color="primary"
                    size="small"
                  />
                )}
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#eab308" }} />
                  <Typography variant="caption" color="text.secondary">Pendiente</Typography>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#22c55e", ml: 1 }} />
                  <Typography variant="caption" color="text.secondary">Aceptada</Typography>
                </Box>
              </Stack>
            </Box>

            <CardContent sx={{ p: 2 }}>
              <Grid container columns={7} sx={{ textAlign: "center", mb: 1 }}>
                {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((d, i) => (
                  <Grid size={{ xs: 1 }} key={i}>
                    <Typography variant="caption" fontWeight={800} color="text.secondary">
                      {d}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              <Grid container columns={7} spacing={1}>
                {Array.from({ length: firstDayOfMonthIndex }).map((_, i) => (
                  <Grid size={{ xs: 1 }} key={`empty-${i}`}>
                    <Box sx={{ minHeight: 70, bgcolor: "#fafafa", borderRadius: 2, opacity: 0.4 }} />
                  </Grid>
                ))}

                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const dayNum = i + 1;
                  const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                  const dayAppointments = appointmentsByDate[dateString];
                  const isToday = dateString === todayStr;
                  const isSelected = selectedDayFilter === dateString;

                  // Color map per status for calendar chips
                  const chipColors: Record<string, { bg: string; color: string; border: string }> = {
                    pendiente:  { bg: "#fef9c3", color: "#854d0e", border: "#fde047" },
                    aceptada:   { bg: "#dcfce7", color: "#166534", border: "#86efac" },
                    completada: { bg: "#e0f2fe", color: "#0369a1", border: "#7dd3fc" },
                    cancelada:  { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
                  };

                  return (
                    <Grid size={{ xs: 1 }} key={dayNum}>
                      <Box
                        onClick={() => {
                          if (isSelected) {
                            setSelectedDayFilter(null);
                          } else {
                            setSelectedDayFilter(dateString);
                          }
                        }}
                        sx={{
                          minHeight: 110,
                          p: 0.75,
                          borderRadius: 2,
                          border: isSelected ? "2px solid #2563eb" : isToday ? "2px solid #93c5fd" : "1px solid #e2e8f0",
                          bgcolor: isSelected ? "#eff6ff" : isToday ? "#f0f9ff" : "#fff",
                          cursor: "pointer",
                          transition: "all 0.15s ease-in-out",
                          display: "flex",
                          flexDirection: "column",
                          "&:hover": {
                            bgcolor: isSelected ? "#dbeafe" : "#f8fafc",
                            borderColor: isSelected ? "#2563eb" : "#cbd5e1",
                          },
                        }}
                      >
                        {/* Day number + total badge */}
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography
                            variant="caption"
                            fontWeight={isToday ? 900 : 700}
                            color={isToday ? "primary.main" : "text.primary"}
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              bgcolor: isToday ? "#2563eb" : "transparent",
                              color: isToday ? "#fff" : undefined,
                            }}
                          >
                            {dayNum}
                          </Typography>
                          {dayAppointments && (
                            <Box
                              sx={{
                                bgcolor: "#6366f1",
                                color: "#fff",
                                borderRadius: "10px",
                                fontSize: "9px",
                                fontWeight: 800,
                                px: 0.7,
                                py: 0.1,
                                lineHeight: 1.6,
                              }}
                            >
                              {dayAppointments.total}
                            </Box>
                          )}
                        </Box>

                        {/* Individual appointment chips */}
                        {dayAppointments && (
                          <Box
                            sx={{
                              overflowY: "auto",
                              maxHeight: 80,
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                              // Custom thin scrollbar
                              "&::-webkit-scrollbar": { width: "3px" },
                              "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 },
                            }}
                            onClick={(e) => e.stopPropagation()} // prevent day-filter when clicking chip area
                          >
                            {dayAppointments.items.map((appt) => {
                              const c = chipColors[appt.status] || chipColors.pendiente;
                              return (
                                <Box
                                  key={appt.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedAppointment(appt);
                                    setOpenDetailDialog(true);
                                  }}
                                  sx={{
                                    bgcolor: c.bg,
                                    color: c.color,
                                    border: `1px solid ${c.border}`,
                                    borderRadius: "4px",
                                    px: 0.6,
                                    py: "1px",
                                    fontSize: "9.5px",
                                    fontWeight: 700,
                                    lineHeight: 1.5,
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    transition: "all 0.1s",
                                    "&:hover": {
                                      opacity: 0.8,
                                      transform: "scale(1.02)",
                                    },
                                  }}
                                  title={`${appt.timeSlot} - ${appt.clientName} (${appt.serviceName})`}
                                >
                                  {appt.timeSlot && (
                                    <span style={{ opacity: 0.75, marginRight: "3px" }}>{appt.timeSlot.replace(/ (AM|PM)/, (m: string) => m.trim())}</span>
                                  )}
                                  {appt.clientName?.split(" ")[0]}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          {/* Appointments Table Card */}
          <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0" }}>
            {/* Filters Bar */}
            <Box sx={{ p: 2.5, borderBottom: "1px solid #e2e8f0" }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
                <Tabs
                  value={currentTab}
                  onChange={(_, val) => setCurrentTab(val)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ minHeight: 40 }}
                >
                  <Tab label={`Todas (${appointments.length})`} value="todas" sx={{ fontWeight: 700, textTransform: "none" }} />
                  <Tab label={`Pendientes (${stats.pendientes})`} value="pendientes" sx={{ fontWeight: 700, textTransform: "none", color: "#ca8a04" }} />
                  <Tab label={`Aceptadas (${stats.aceptadas})`} value="aceptadas" sx={{ fontWeight: 700, textTransform: "none", color: "#16a34a" }} />
                  <Tab label={`Hoy (${stats.hoy})`} value="hoy" sx={{ fontWeight: 700, textTransform: "none" }} />
                  <Tab label={`Completadas (${stats.completadas})`} value="completadas" sx={{ fontWeight: 700, textTransform: "none" }} />
                  <Tab label={`Canceladas (${stats.canceladas})`} value="canceladas" sx={{ fontWeight: 700, textTransform: "none" }} />
                </Tabs>

                <Stack direction="row" spacing={1.5}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Modalidad</InputLabel>
                    <Select
                      value={modalityFilter}
                      label="Modalidad"
                      onChange={(e) => setModalityFilter(e.target.value)}
                    >
                      <MenuItem value="todas">Todas</MenuItem>
                      <MenuItem value="virtual">Virtual</MenuItem>
                      <MenuItem value="presencial">Presencial</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    placeholder="Buscar por cliente, cédula o código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1, fontSize: 18 }} />,
                    }}
                    sx={{ minWidth: 240 }}
                  />
                </Stack>
              </Stack>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : filteredAppointments.length === 0 ? (
              <Box textAlign="center" py={8} color="text.secondary">
                <EventAvailableIcon sx={{ fontSize: 60, opacity: 0.2, mb: 1 }} />
                <Typography variant="body1" fontWeight={700}>
                  No se encontraron citas con los filtros seleccionados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedDayFilter ? `No hay citas para el día ${selectedDayFilter}` : "Intenta cambiar los filtros o la pestaña."}
                </Typography>
                {selectedDayFilter && (
                  <Button size="small" onClick={() => setSelectedDayFilter(null)} sx={{ mt: 1 }}>
                    Quitar filtro de fecha
                  </Button>
                )}
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Código</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Cliente & Contacto</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Especialidad</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Modalidad & Enlace de Llamada</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Fecha & Turno</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAppointments.map((appt) => {
                      const isPresencial = appt.modality === "presencial";

                      return (
                        <TableRow key={appt.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={800} color="primary.main" fontFamily="monospace">
                              {appt.code || "CITA-SIN-COD"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {appt.clientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              ✉️ {appt.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              📞 {appt.phone}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {appt.serviceName}
                            </Typography>
                            <Chip
                              label={appt.vertical?.toUpperCase()}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "10px", height: 20, mt: 0.5 }}
                            />
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5}>
                              <Stack direction="row" alignItems="center" spacing={0.8}>
                                {isPresencial ? (
                                  <StorefrontIcon sx={{ fontSize: 18, color: "#059669" }} />
                                ) : (
                                  <VideoCameraFrontIcon sx={{ fontSize: 18, color: "#0284c7" }} />
                                )}
                                <Typography variant="body2" fontWeight={700} color={isPresencial ? "#047857" : "#0369a1"}>
                                  {isPresencial ? "Presencial" : "Virtual (Videollamada)"}
                                </Typography>
                              </Stack>

                              {isPresencial ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  🏢 {appt.branch || "Sede Principal"}
                                </Typography>
                              ) : (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  {appt.meetUrl ? (
                                    <>
                                      <Tooltip title="Entrar a la videollamada desde el panel">
                                        <Chip
                                          icon={<VideoCameraFrontIcon sx={{ fontSize: "14px !important" }} />}
                                          label="Entrar a la Llamada"
                                          size="small"
                                          color="primary"
                                          clickable
                                          component="a"
                                          href={appt.meetUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ fontWeight: 800, fontSize: "10px", height: 24, px: 0.5 }}
                                        />
                                      </Tooltip>
                                      <Tooltip title="Copiar enlace de llamada">
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(appt.meetUrl!, "Enlace de Videollamada")}
                                          sx={{ p: 0.3 }}
                                        >
                                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Chip
                                      label="Se generará al confirmar"
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                      sx={{ fontSize: "10px", height: 22 }}
                                    />
                                  )}
                                </Box>
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight={800}>
                              {appt.date}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} color="primary">
                              ⏰ {appt.timeSlot}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={statusLabels[appt.status] || appt.status}
                              color={statusColors[appt.status] || "default"}
                              size="small"
                              sx={{ fontWeight: 800, fontSize: "11px" }}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              {/* Accept / Confirm Button */}
                              {appt.status === "pendiente" && (
                                <Tooltip title="Confirmar Cita y Enviar Notificación al Correo">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleInitiateAcceptAppointment(appt)}
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* Complete Button */}
                              {appt.status === "aceptada" && (
                                <Tooltip title="Marcar como Completada">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleUpdateStatus(appt.id!, "completada")}
                                  >
                                    <TaskAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* Direct Email Sender */}
                              {appt.email && (
                                <Tooltip title="Enviar detalles por Correo">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => openMailto(appt)}
                                  >
                                    <EmailIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* WhatsApp Direct */}
                              <Tooltip title="Contactar por WhatsApp">
                                <IconButton
                                  size="small"
                                  sx={{ color: "#25D366" }}
                                  onClick={() => openWhatsApp(appt)}
                                >
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* View Detail */}
                              <Tooltip title="Ver Detalles y Gestionar">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedAppointment(appt);
                                    setOpenDetailDialog(true);
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* Cancel Button */}
                              {appt.status !== "cancelada" && appt.status !== "completada" && (
                                <Tooltip title="Cancelar Cita">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleUpdateStatus(appt.id!, "cancelada")}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* Delete permanently */}
                              <Tooltip title="Eliminar del Registro">
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={() => handleDelete(appt.id!)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </>
      )}

      {/* ========================================================================= */}
      {/* ==================== TAB 2: CONFIGURACIÓN DE SEDES ====================== */}
      {/* ========================================================================= */}
      {mainTab === "sedes" && (
        <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0" }}>
          <Box sx={{ p: 3, borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={800} display="flex" alignItems="center" gap={1}>
                <PlaceIcon color="primary" />
                Sedes de Atención Presencial ({sedes.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Las sedes activas aparecerán inmediatamente disponibles para los usuarios en el portal de citas presenciales de next-front.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              {sedes.length === 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AutorenewIcon />}
                  onClick={handleSeedDefaultSedes}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Cargar Sedes Predeterminadas
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenNewSede}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                + Nueva Sede
              </Button>
            </Stack>
          </Box>

          {loadingSedes ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : sedes.length === 0 ? (
            <Box textAlign="center" py={8} px={3} color="text.secondary">
              <StorefrontIcon sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
              <Typography variant="h6" fontWeight={800} color="text.primary">
                No hay sedes configuradas en la base de datos
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={500} mx="auto" mt={1} mb={3}>
                Puedes cargar las sedes base en Firestore para comenzar a administrarlas dinámicamente.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleSeedDefaultSedes}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                Cargar Sedes Base (Bogotá, Medellín, Cali)
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Nombre de la Sede</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Ciudad</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Dirección</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Horario de Atención</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Teléfono</TableCell>
                    <TableCell sx={{ fontWeight: 800 }} align="center">Estado (Portal)</TableCell>
                    <TableCell sx={{ fontWeight: 800 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sedes.map((sede) => (
                    <TableRow key={sede.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={800}>
                          {sede.name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={sede.city}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 700, fontSize: "11px" }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {sede.address}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {sede.schedule}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {sede.phone || "N/A"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title={sede.active ? "Sede activa (visible en next-front)" : "Sede inactiva (oculta en next-front)"}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={sede.active}
                                onChange={() => handleToggleSedeActive(sede)}
                                color="success"
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="caption" fontWeight={700} color={sede.active ? "success.main" : "text.disabled"}>
                                {sede.active ? "Activa" : "Inactiva"}
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                        </Tooltip>
                      </TableCell>

                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Editar Sede">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditSede(sede)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar Sede">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSede(sede.id, sede.name)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {/* ========================================================================= */}
      {/* ===================== DIALOG: ACEPTAR / CONFIRMAR CITA =================== */}
      {/* ========================================================================= */}
      <Dialog
        open={openAcceptDialog}
        onClose={() => !processingAccept && setOpenAcceptDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {appointmentToAccept && (
          <>
            <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
              Confirmar Cita & Notificar al Cliente
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2.5 }}>
              <Stack spacing={2.5}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Al confirmar la cita, el estado cambiará a <strong>Aceptada</strong>, se le enviará la notificación con el enlace al correo <strong>{appointmentToAccept.email}</strong> y tendrás el botón directo en tu panel para unirte a la llamada.
                </Alert>

                <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" fontWeight={800}>
                    {appointmentToAccept.clientName} ({appointmentToAccept.code})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    📅 {appointmentToAccept.date} | ⏰ {appointmentToAccept.timeSlot} | {appointmentToAccept.serviceName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ✉️ {appointmentToAccept.email} | 📞 {appointmentToAccept.phone}
                  </Typography>
                </Box>

                {appointmentToAccept.modality === "virtual" && (
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
                      ENLACE DE LA SALA DE VIDEOLLAMADA (PARA CLIENTE Y ASESOR)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="URL de la Llamada"
                      value={assignedCallUrl}
                      onChange={(e) => setAssignedCallUrl(e.target.value)}
                      placeholder="https://meet.google.com/... o sala interactiva"
                    />

                    <Stack direction="row" spacing={1} mt={1}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          const clean = appointmentToAccept.code.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
                          setAssignedCallUrl(`https://meet.jit.si/GrupoNext-${clean}`);
                        }}
                        sx={{ fontSize: "11px", textTransform: "none" }}
                      >
                        ⚡ Sala Instantánea Automática
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        href="https://meet.google.com/new"
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        sx={{ fontSize: "11px", textTransform: "none" }}
                      >
                        Crear en Google Meet
                      </Button>
                    </Stack>
                  </Box>
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={sendEmailNotification}
                      onChange={(e) => setSendEmailNotification(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={700}>
                      Enviar correo de confirmación con el enlace de la cita al cliente
                    </Typography>
                  }
                />
              </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setOpenAcceptDialog(false)} disabled={processingAccept} color="inherit">
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmAndNotify}
                disabled={processingAccept}
                startIcon={<CheckCircleIcon />}
                sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
              >
                {processingAccept ? "Confirmando..." : "Confirmar Cita & Notificar"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ========================================================================= */}
      {/* ========================== DIALOG: DETALLE CITA ========================= */}
      {/* ========================================================================= */}
      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {selectedAppointment && (
          <>
            {/* Colored header banner based on status */}
            {(() => {
              const bannerColors: Record<string, { bg: string; text: string; accent: string }> = {
                pendiente:  { bg: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)", text: "#fff", accent: "#fef3c7" },
                aceptada:   { bg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", text: "#fff", accent: "#dcfce7" },
                completada: { bg: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)", text: "#fff", accent: "#e0f2fe" },
                cancelada:  { bg: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)", text: "#fff", accent: "#fee2e2" },
              };
              const bc = bannerColors[selectedAppointment.status] || bannerColors.pendiente;
              return (
                <Box sx={{ background: bc.bg, px: 3, pt: 2.5, pb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="overline" sx={{ color: bc.text, opacity: 0.85, fontWeight: 700, fontSize: "10px", letterSpacing: 1.5 }}>
                        DETALLE DE CITA
                      </Typography>
                      <Typography variant="h6" sx={{ color: bc.text, fontWeight: 900, lineHeight: 1.2, mt: 0.2 }}>
                        {selectedAppointment.clientName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: bc.text, opacity: 0.9, fontFamily: "monospace", fontWeight: 700 }}>
                        {selectedAppointment.code}
                      </Typography>
                    </Box>
                    <Chip
                      label={statusLabels[selectedAppointment.status]}
                      size="small"
                      sx={{ bgcolor: "rgba(255,255,255,0.25)", color: bc.text, fontWeight: 800, border: "1px solid rgba(255,255,255,0.4)", backdropFilter: "blur(4px)" }}
                    />
                  </Stack>

                  {/* Quick time/date strip */}
                  <Stack direction="row" spacing={2} mt={1.5}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <AccessTimeIcon sx={{ fontSize: 14, color: bc.text, opacity: 0.9 }} />
                      <Typography variant="caption" sx={{ color: bc.text, fontWeight: 700, opacity: 0.95 }}>
                        {selectedAppointment.timeSlot}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CalendarMonthIcon sx={{ fontSize: 14, color: bc.text, opacity: 0.9 }} />
                      <Typography variant="caption" sx={{ color: bc.text, fontWeight: 700, opacity: 0.95 }}>
                        {selectedAppointment.date}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {selectedAppointment.modality === "presencial" ? (
                        <StorefrontIcon sx={{ fontSize: 14, color: bc.text, opacity: 0.9 }} />
                      ) : (
                        <VideoCameraFrontIcon sx={{ fontSize: 14, color: bc.text, opacity: 0.9 }} />
                      )}
                      <Typography variant="caption" sx={{ color: bc.text, fontWeight: 700, opacity: 0.95 }}>
                        {selectedAppointment.modality === "presencial" ? "Presencial" : "Virtual"}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              );
            })()}

            <DialogContent sx={{ pt: 2.5, pb: 1 }}>
              <Stack spacing={2}>

                {/* Client info */}
                <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={800} display="block" mb={1} letterSpacing={0.8}>
                    INFORMACIÓN DEL SOLICITANTE
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 12 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BusinessIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                        <Typography variant="body2" fontWeight={700}>
                          {selectedAppointment.documentType}: <span style={{ fontFamily: "monospace" }}>{selectedAppointment.documentNumber}</span>
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PhoneIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                        <Typography variant="body2" color="text.secondary">{selectedAppointment.phone}</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmailIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                        <Typography variant="body2" color="text.secondary" noWrap>{selectedAppointment.email}</Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Service info */}
                <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={800} display="block" mb={1} letterSpacing={0.8}>
                    ESPECIALIDAD / SERVICIO
                  </Typography>
                  <Typography variant="body1" fontWeight={800}>{selectedAppointment.serviceName}</Typography>
                  <Chip
                    label={selectedAppointment.vertical?.toUpperCase()}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mt: 0.5, fontSize: "10px", height: 20, fontWeight: 700 }}
                  />
                </Paper>

                {/* Location / Modality */}
                <Paper elevation={0} sx={{ p: 2, bgcolor: selectedAppointment.modality === "presencial" ? "#f0fdf4" : "#f0f9ff", borderRadius: 2, border: `1px solid ${selectedAppointment.modality === "presencial" ? "#bbf7d0" : "#bae6fd"}` }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={800} display="block" mb={0.5} letterSpacing={0.8}>
                    MODALIDAD Y UBICACIÓN
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {selectedAppointment.modality === "presencial" ? (
                      <StorefrontIcon sx={{ color: "#059669" }} />
                    ) : (
                      <VideoCameraFrontIcon sx={{ color: "#0284c7" }} />
                    )}
                    <Typography variant="body1" fontWeight={800} color={selectedAppointment.modality === "presencial" ? "#047857" : "#0369a1"}>
                      {selectedAppointment.modality === "presencial"
                        ? `Presencial — ${selectedAppointment.branch || "Sede Principal"}`
                        : "Cita Virtual por Videollamada"}
                    </Typography>
                  </Stack>
                </Paper>

                {/* VIDEO CALL SECTION IN DIALOG */}
                {selectedAppointment.modality === "virtual" && (
                  <Paper sx={{ p: 2, bgcolor: "#eff6ff", borderRadius: 2, border: "1px solid #bfdbfe" }} elevation={0}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="primary.dark" fontWeight={800} display="flex" alignItems="center" gap={1}>
                        <VideoCameraFrontIcon sx={{ fontSize: 18 }} />
                        SALA DE VIDEOLLAMADA
                      </Typography>
                      <Chip label={selectedAppointment.meetUrl ? "Enlace Listo" : "Pendiente"} size="small" color={selectedAppointment.meetUrl ? "success" : "warning"} sx={{ height: 20, fontSize: "10px", fontWeight: 700 }} />
                    </Stack>

                    <Typography variant="body2" fontFamily="monospace" fontWeight={700} color="primary.main" sx={{ wordBreak: "break-all", mb: 1.5 }}>
                      {selectedAppointment.meetUrl || "Sin enlace asignado todavía"}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {selectedAppointment.meetUrl && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          href={selectedAppointment.meetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                        >
                          Entrar a la Llamada
                        </Button>
                      )}

                      {selectedAppointment.meetUrl && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => copyToClipboard(selectedAppointment.meetUrl!, "Enlace de Llamada")}
                          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                        >
                          Copiar Enlace
                        </Button>
                      )}

                      <Button
                        variant="text"
                        color="inherit"
                        size="small"
                        startIcon={<AutorenewIcon />}
                        onClick={() => handleInitiateAcceptAppointment(selectedAppointment)}
                        sx={{ textTransform: "none", fontSize: "12px" }}
                      >
                        Cambiar / Reasignar Sala
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {selectedAppointment.notes && (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: "#fffbeb", borderRadius: 2, border: "1px solid #fde68a" }}>
                    <Typography variant="caption" color="#92400e" fontWeight={800} display="block" mb={0.5} letterSpacing={0.8}>
                      📝 NOTAS / MOTIVO DE LA CONSULTA
                    </Typography>
                    <Typography variant="body2" mt={0.5}>
                      {selectedAppointment.notes}
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ p: 2, flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <Button
                  startIcon={<EmailIcon />}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={() => openMailto(selectedAppointment)}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                >
                  Correo
                </Button>

                <Button
                  startIcon={<WhatsAppIcon />}
                  color="success"
                  variant="outlined"
                  size="small"
                  onClick={() => openWhatsApp(selectedAppointment)}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                >
                  WhatsApp
                </Button>

                <Button
                  startIcon={<CreditCardIcon />}
                  color="secondary"
                  variant="contained"
                  size="small"
                  onClick={() => openCreditOnboardingWhatsApp(selectedAppointment)}
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                >
                  Enviar Requisitos de Crédito ($250k & Contrato)
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {selectedAppointment.status === "pendiente" && (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => {
                      setOpenDetailDialog(false);
                      handleInitiateAcceptAppointment(selectedAppointment);
                    }}
                    sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                  >
                    Confirmar
                  </Button>
                )}
                {selectedAppointment.status === "aceptada" && (
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    startIcon={<TaskAltIcon />}
                    onClick={() => handleUpdateStatus(selectedAppointment.id!, "completada")}
                    sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
                  >
                    Completada
                  </Button>
                )}
                {selectedAppointment.status !== "cancelada" && selectedAppointment.status !== "completada" && (
                  <Button
                    color="error"
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => handleUpdateStatus(selectedAppointment.id!, "cancelada")}
                    sx={{ borderRadius: 2, textTransform: "none" }}
                  >
                    Cancelar
                  </Button>
                )}
                <Button onClick={() => setOpenDetailDialog(false)} color="inherit" size="small" sx={{ textTransform: "none" }}>
                  Cerrar
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ========================================================================= */}
      {/* ====================== DIALOG: CREAR / EDITAR SEDE ====================== */}
      {/* ========================================================================= */}
      <Dialog
        open={openSedeDialog}
        onClose={() => setOpenSedeDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleSaveSede}>
          <DialogTitle sx={{ fontWeight: 800 }}>
            {editingSede ? "Editar Sede de Atención" : "Registrar Nueva Sede de Atención"}
          </DialogTitle>
          <Divider />

          <DialogContent sx={{ pt: 2.5 }}>
            <Stack spacing={2.5}>
              <TextField
                required
                fullWidth
                label="Nombre de la Sede"
                placeholder="Ej: Sede Principal Bogotá o Sede Barranquilla - Prado"
                value={sedeName}
                onChange={(e) => setSedeName(e.target.value)}
              />

              <TextField
                required
                fullWidth
                label="Ciudad"
                placeholder="Ej: Bogotá D.C., Medellín, Cali, Barranquilla..."
                value={sedeCity}
                onChange={(e) => setSedeCity(e.target.value)}
              />

              <TextField
                required
                fullWidth
                label="Dirección Física Completa"
                placeholder="Ej: Calle 100 # 15-20, Torre Financiera, Piso 8"
                value={sedeAddress}
                onChange={(e) => setSedeAddress(e.target.value)}
              />

              <TextField
                required
                fullWidth
                label="Horario de Atención"
                placeholder="Ej: Lunes a Viernes 8:00 AM - 5:00 PM, Sábados 8:00 AM - 12:00 PM"
                value={sedeSchedule}
                onChange={(e) => setSedeSchedule(e.target.value)}
              />

              <TextField
                fullWidth
                label="Teléfono o Línea de Contacto (Opcional)"
                placeholder="Ej: +57 (601) 745-0000 o 310 123 4567"
                value={sedePhone}
                onChange={(e) => setSedePhone(e.target.value)}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={sedeActive}
                    onChange={(e) => setSedeActive(e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={700}>
                    Sede Habilitada (Disponible para visitas en el portal de citas)
                  </Typography>
                }
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setOpenSedeDialog(false)} color="inherit">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
            >
              {editingSede ? "Guardar Cambios" : "Crear Sede"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
